import { useI18n } from '../../i18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/shared/PageHeader';
import { PageContainer } from '../../components/shared/PageContainer';
import { ActionButton } from '../../components/shared/ActionButton';
import { ExportButton } from '../../components/shared/ExportButton';
import { DownloadIcon } from '../../components/icons';
import { useExport } from '../../hooks/useExport';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { Pagination } from '../../components/shared/Pagination';
import { useStores } from '../../hooks/useStores';
import {
  TransactionStatsRow,
  TransactionFilterBar,
  TransactionFilters,
  TransactionsTable,
  TransactionRow,
  SmartInsightsCard,
  RegionalHeatmap,
} from './components';
import { usePayments } from '../../hooks';
import { usePaymentStats } from '../../hooks/usePayments';
import { useStoreSelectionStore } from '../../stores';
import { useActiveCurrency } from '../../hooks/useActiveCurrency';
import { useCurrencyScope, useMoney } from '../../providers/CurrencyProvider';
// APP_CURRENCY vẫn cần cho các helper cấp module (formatTxnAmount, mapPaymentToRow)
// — chỗ đó không gọi hook được.
import { APP_CURRENCY } from '../../utils/currency';
import { PaymentStatus } from '@conai/shared-types';

type PaymentApiRow = {
  id: string;
  transaction_id?: string;
  provider_transaction_id?: string;
  order_code?: string;
  amount?: string | number;
  currency?: string;
  payment_method?: string;
  payment_status?: string;
  status?: string;
  requested_at?: string;
  confirmed_at?: string | null;
  created_at?: string;
  order?: { order_number?: string } | string | null;
};

/**
 * Bug #117: dòng cửa hàng đọc từ `/stores/` — cùng field mà màn Stores dùng để
 * quyết định trạng thái (`business_status`). Khai đủ ở đây thay vì `{ id, name }`
 * như trước, vì thiếu field trạng thái chính là lý do KPI "Active Stores" đếm cả
 * cửa hàng đã ngừng hoạt động.
 *
 * KHÔNG có `status`: model Store không tồn tại cột đó (BE 26/08/2026).
 */
type StoreApiRow = {
  id: string;
  name: string;
  business_status?: string | null;
  city?: string;
  /** Chưa có trong model Store — giữ optional để không phải sửa lại khi BE thêm. */
  region?: string;
};

type ListEnvelope<T> = {
  results?: T[];
  items?: T[];
  data?: T[] | { results?: T[]; items?: T[] };
  count?: number;
  total?: number;
  meta?: { count?: number };
};

function extractList<T>(payload: unknown): T[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as T[];

  const raw = payload as any;
  // Trường hợp Axios response bọc ngoài hoặc dữ liệu có dạng { data: [...] } hoặc { data: { data: [...] } }
  const dataField = raw.data;
  if (dataField) {
    if (Array.isArray(dataField)) return dataField as T[];
    if (typeof dataField === 'object') {
      if (Array.isArray(dataField.data)) return dataField.data;
      if (Array.isArray(dataField.results)) return dataField.results;
      if (Array.isArray(dataField.items)) return dataField.items;
    }
  }

  const resultsField = raw.results;
  if (Array.isArray(resultsField)) return resultsField as T[];

  const itemsField = raw.items;
  if (Array.isArray(itemsField)) return itemsField as T[];

  return [];
}

function extractCount<T>(payload: unknown, fallback: number): number {
  if (!payload) return fallback;
  if (Array.isArray(payload)) return payload.length;

  const raw = payload as any;
  const countVal = raw.count ?? raw.total ?? raw.data?.total ?? raw.data?.count;
  if (typeof countVal === 'number') return countVal;
  
  return fallback;
}

function toPaymentStatus(value: unknown): PaymentStatus {
  const raw = typeof value === 'string' ? value.trim().toUpperCase() : '';
  if (raw === 'PAID') return PaymentStatus.COMPLETED;
  if (raw === 'AUTHORIZED') return PaymentStatus.PROCESSING;
  if (raw === 'FAILED' || raw === 'DECLINED') return PaymentStatus.FAILED;
  if (raw === 'REFUNDED') return PaymentStatus.REFUNDED;
  if (raw === 'CANCELLED') return PaymentStatus.CANCELLED;
  if (raw === 'PROCESSING') return PaymentStatus.PROCESSING;
  if (raw === 'COMPLETED') return PaymentStatus.COMPLETED;
  if (raw === 'PENDING') return PaymentStatus.PENDING;
  return PaymentStatus.PENDING;
}

function paymentStatusLabel(status: string, t: (key: string) => string): string {
  switch (status) {
    case 'COMPLETED': return t('completed');
    case 'PROCESSING': return t('processing');
    case 'PENDING': return t('pending');
    case 'FAILED': return t('declined');
    case 'REFUNDED': return t('refunded');
    case 'CANCELLED': return t('cancelled');
    default: return String(status);
  }
}

/** Bug #95: format số tiền theo currency thật của giao dịch (mặc định là đơn vị
 * tiền tệ của app) — không hardcode USD/$ trong khi dòng "Currency Unit" ghi khác. */
function formatTxnAmount(amount: number, currency?: string): string {
  const code = (currency || APP_CURRENCY).trim().toUpperCase();
  try {
    return new Intl.NumberFormat(code === 'VND' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'VND' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${code}`;
  }
}

function toAmount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function mapPaymentToRow(payment: PaymentApiRow): TransactionRow {
  const orderFromObject =
    payment.order && typeof payment.order === 'object' && 'order_number' in payment.order
      ? payment.order.order_number
      : undefined;
  const status = toPaymentStatus(payment.payment_status ?? payment.status);
  const code =
    payment.provider_transaction_id ?? payment.transaction_id ?? payment.order_code ?? String(payment.id);

  return {
    id: payment.id,
    code: `#${code}`,
    // Bug #118: đây là mã tham chiếu ĐƠN HÀNG (order_code / order.order_number).
    orderRef: payment.order_code ?? orderFromObject ?? 'Unknown Order',
    storeRegion: payment.currency ?? APP_CURRENCY,
    amount: toAmount(payment.amount),
    paymentMethod: payment.payment_method ?? 'CARD',
    paymentMasked: payment.provider_transaction_id ?? undefined,
    status,
    timestamp: payment.requested_at ?? payment.confirmed_at ?? payment.created_at ?? new Date().toISOString(),
  };
}

// ============================================
// Component
// ============================================

export function TransactionsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  // Bug #61: trang Reports điều hướng sang đây kèm ?search=<từ khoá> — phải đọc
  // và áp dụng, nếu không thì ô "Search transactions" bên Reports vô nghĩa.
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get('search') ?? '').trim();
  const { selectedStoreId } = useStoreSelectionStore();

  // Tiền tệ hiển thị theo cửa hàng đang chọn (sheet 2 #75). Không chọn cửa
  // hàng = xem toàn chuỗi thì rơi về mặc định; giao dịch có `currency` riêng
  // nên chỗ nào đọc được của bản ghi thì ưu tiên dùng của bản ghi.
  useCurrencyScope(useActiveCurrency());
  const money = useMoney();
  // State — filter Location khởi tạo theo store tổng trên header (lần đầu mở),
  // sau đó filter của bảng TOÀN QUYỀN: chọn "All Locations" là xem tất cả,
  // không rơi ngược về store tổng nữa.
  const [filters, setFilters] = useState<TransactionFilters>(() => ({
    location: selectedStoreId || null,
    status: null,
    dateRange: 'all',
  }));

  // Store selector (zustand persist) có thể hydrate sau lần render đầu — đồng bộ
  // giá trị khởi tạo một lần, nhưng chỉ khi user CHƯA tự đụng vào filter.
  const filterTouchedRef = useRef(false);
  useEffect(() => {
    if (!filterTouchedRef.current) {
      setFilters((prev) => ({ ...prev, location: selectedStoreId || null }));
    }
  }, [selectedStoreId]);

  const effectiveStore = filters.location || undefined;
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTxn, setSelectedTxn] = useState<TransactionRow | null>(null);
  useLockBodyScroll(!!selectedTxn);
  // 12/trang cho cả hai chế độ — lưới 3 cột luôn tròn 4 hàng, không lệch ô.
  const pageSize = 12;

  const { data: storesData } = useStores({ page_size: 100 } as any);

  const dateParams = useMemo(() => {
    if (!filters.dateRange || filters.dateRange === 'all') return {};

    // Khoảng tự chọn. Chỉ lọc khi đã chọn ĐỦ hai đầu — thiếu một đầu mà vẫn
    // gửi thì kết quả là "từ ngày X tới bây giờ", không phải điều người dùng
    // đang định làm khi họ mới gõ xong ô đầu tiên.
    if (filters.dateRange === 'custom') {
      if (!filters.dateFrom || !filters.dateTo) return {};
      return {
        created_at__gte: new Date(`${filters.dateFrom}T00:00:00`).toISOString(),
        // Trọn ngày cuối: người chọn "đến 09/09" muốn CẢ ngày 09/09, chứ không
        // phải tới 00:00 sáng hôm đó — cắt ở nửa đêm là nuốt mất một ngày.
        created_at__lte: new Date(`${filters.dateTo}T23:59:59.999`).toISOString(),
      };
    }

    const end = new Date().toISOString();
    const now = new Date();
    let start = '';
    if (filters.dateRange === 'today') {
      now.setHours(0, 0, 0, 0);
      start = now.toISOString();
    } else if (filters.dateRange === 'last_7_days') {
      now.setDate(now.getDate() - 7);
      start = now.toISOString();
    } else if (filters.dateRange === 'last_30_days') {
      now.setDate(now.getDate() - 30);
      start = now.toISOString();
    } else if (filters.dateRange === 'last_90_days') {
      now.setDate(now.getDate() - 90);
      start = now.toISOString();
    } else if (filters.dateRange === 'this_year') {
      start = new Date(now.getFullYear(), 0, 1).toISOString();
    }
    return {
      created_at__gte: start || undefined,
      created_at__lte: end,
    };
  }, [filters.dateRange, filters.dateFrom, filters.dateTo]);

  const {
    data: paymentsData,
    isLoading,
    error,
  } = usePayments({
    page: currentPage,
    page_size: pageSize,
    payment_status: filters.status || undefined,
    store: effectiveStore,
    // Bug #61: chuyển từ khoá search xuống server (nếu BE hỗ trợ) + lọc lại client bên dưới.
    ...(searchQuery ? { search: searchQuery } : {}),
    ...dateParams,
  } as any);

  // KPI cards đọc /payments/stats/ — BE đếm trên TOÀN BỘ giao dịch khớp filter,
  // không phải cộng 10 dòng của trang hiện tại như trước.
  const { data: paymentStatsData } = usePaymentStats({
    store_id: effectiveStore,
    start_date: (dateParams as { created_at__gte?: string }).created_at__gte?.slice(0, 10),
    end_date: (dateParams as { created_at__lte?: string }).created_at__lte?.slice(0, 10),
  });

  /**
   * Bug #117: một nguồn dữ liệu duy nhất cho cửa hàng, dùng chung với màn Stores.
   *
   * Trước đây thẻ KPI "Active Stores" đếm `storeOptions.length` — tức là TẤT CẢ
   * cửa hàng trả về từ `/stores/`, kể cả INACTIVE và MAINTENANCE. Màn Stores lại
   * chỉ coi `ACTIVE` là đang hoạt động (xem `mapStoreStatus` trong StoresPage:
   * ACTIVE→ONLINE, INACTIVE→OFFLINE, MAINTENANCE→MAINTENANCE), nên hai màn nói
   * hai con số khác nhau trên cùng một tập dữ liệu.
   */
  const storeList = useMemo(
    () => extractList<StoreApiRow>(storesData),
    [storesData]
  );

  // Dropdown Location vẫn liệt kê ĐỦ cửa hàng — lọc giao dịch theo một cửa hàng
  // tạm ngừng hoạt động là nhu cầu có thật, khác với việc đếm số đang hoạt động.
  const storeOptions = useMemo(
    () => storeList.map((s) => ({ id: s.id, name: s.name })),
    [storeList]
  );

  /** Bug #117: cùng điều kiện "active" với màn Stores — đọc `business_status`
   * (field duy nhất BE có) và chỉ ACTIVE mới được tính. */
  const activeStores = useMemo(
    () => storeList.filter((s) => s.business_status === 'ACTIVE'),
    [storeList]
  );

  const transactions = useMemo(() => {
    let list = extractList<PaymentApiRow>(paymentsData).map(mapPaymentToRow);

    // 1. Local filter status
    if (filters.status) {
      list = list.filter((t) => t.status === filters.status);
    }

    // Location is filtered server-side via the `store` query param (see usePayments
     // call above). The previous client-side re-filter compared the store name against
     // t.orderRef (which is the order code), so it wiped all results (#64).

    // Date range is filtered server-side (created_at__gte/__lte). Re-filtering here
    // on a different field (t.timestamp) made the displayed rows mismatch the server
    // `count` used for pagination (#51), so the client-side date filter is removed.

    // Bug #61: lọc client theo từ khoá search (mã giao dịch / mã đơn) làm lớp
    // dự phòng khi BE chưa hỗ trợ param search.
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (row) =>
          row.code.toLowerCase().includes(q) ||
          row.orderRef.toLowerCase().includes(q) ||
          (row.paymentMasked ?? '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [paymentsData, filters.status, filters.location, storeOptions, searchQuery]);

  // KPI từ /payments/stats/ — số thật trên toàn bộ giao dịch khớp filter.
  // Không còn trend bịa (12.5%, "-0.08% from avg"): chưa có baseline thật thì không hiện.
  const stats = useMemo(() => {
    const paidAmount = Number.parseFloat(paymentStatsData?.paid_amount ?? '0') || 0;
    const paidCount = paymentStatsData?.paid_count ?? 0;
    const totalCount = paymentStatsData?.total_count ?? 0;
    const failedCount = paymentStatsData?.status_counts?.['FAILED'] ?? 0;

    const avgAmount = paidCount > 0 ? paidAmount / paidCount : 0;
    const failedRate = totalCount > 0 ? (failedCount / totalCount) * 100 : 0;

    // Bug #117: khu vực cũng đếm trên tập ACTIVE, không phải toàn bộ cửa hàng.
    // `region` không có trong model Store (xem shared-types) nên thực tế luôn
    // rơi về `city` — giữ `region` trước để hôm nào BE bổ sung thì tự dùng.
    const uniqueRegions = new Set(
      activeStores.map((s) => s.region ?? s.city).filter(Boolean)
    );

    return {
      totalVolume: paidAmount >= 1000 ? money.compact(paidAmount) : money.format(paidAmount),
      // Chưa có baseline kỳ trước cho volume — ẩn badge thay vì hiện số bịa.
      totalVolumeTrend: undefined,
      activeStores: activeStores.length,
      activeStoresSubtitle: `${activeStores.length} across ${uniqueRegions.size || 1} regions`,
      avgTransaction: money.format(avgAmount),
      avgTxnSubtitle: `${paidCount.toLocaleString('en-US')} paid transactions`,
      failedRate: `${failedRate.toFixed(2)}%`,
      failedRateTrend: `${failedCount} of ${totalCount}`,
    };
  }, [paymentStatsData, activeStores, money]);

  const totalCount = extractCount<PaymentApiRow>(paymentsData, transactions.length);
  const totalPages = Math.ceil(totalCount / pageSize);

  const { triggerExport, isExporting } = useExport({ endpoint: '/payments/', filePrefix: 'transactions' });

  // Handlers
  const handleRowAction = (transactionId: string) => {
    const txn = transactions.find(t => t.id === transactionId);
    if (txn) {
      setSelectedTxn(txn);
    }
  };

  const handleFullAnalysis = () => {
    navigate('/reports');
  };

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader
        title={t('transactionsLog')}
        subtitle={t('monitorGlobalSalesInventoryMovement')}
        actions={
          <ExportButton onExport={triggerExport} isLoading={isExporting} />
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadTransactions')}</p>
          <p className="mt-1 text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while loading transactions.'}
          </p>
        </div>
      )}

      {/* Stats Row */}
      <div className="mt-6">
        <TransactionStatsRow
          totalVolume={stats.totalVolume}
          totalVolumeTrend={stats.totalVolumeTrend}
          activeStores={stats.activeStores}
          activeStoresSubtitle={stats.activeStoresSubtitle}
          avgTransaction={stats.avgTransaction}
          avgTxnSubtitle={stats.avgTxnSubtitle}
          failedRate={stats.failedRate}
          failedRateTrend={stats.failedRateTrend}
        />
      </div>

      {/* Filter Bar */}
      <div className="mt-6">
        <TransactionFilterBar
          filters={filters}
          onFilterChange={(newFilters) => {
            filterTouchedRef.current = true;
            setFilters(newFilters);
            setCurrentPage(1);
          }}
          viewMode={viewMode}
          onViewModeChange={(m) => { setViewMode(m); setCurrentPage(1); }}
          locations={storeOptions}
        />
      </div>

      {/* Transactions Table / Grid */}
      <div className="mt-6">
        {viewMode === 'grid' ? (
          <div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {transactions.map((txn) => (
                <div key={txn.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between gap-2 border-b border-slate-50 pb-3">
                    <span className="truncate font-bold text-slate-900">{txn.code}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        txn.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700' :
                        txn.status === 'PROCESSING' || txn.status === 'PENDING' ? 'bg-blue-50 text-blue-700' :
                        'bg-red-50 text-red-700'
                      }`}>{paymentStatusLabel(txn.status, t)}</span>
                      <span className="whitespace-nowrap text-xs text-slate-400">{new Date(txn.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-4 text-slate-500">
                      {/* Bug #118: cùng một giá trị thì cùng một nhãn ở cả lưới,
                          bảng và modal chi tiết. */}
                      <span className="shrink-0">{t('orderRef')}:</span>
                      <span className="min-w-0 break-all text-right font-semibold text-slate-700">{txn.orderRef}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>{t('amount')}:</span>
                      <span className="font-bold text-slate-950">
                        {formatTxnAmount(txn.amount, txn.storeRegion)}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>{t('method')}:</span>
                      <span className="text-slate-700 uppercase">{txn.paymentMethod}</span>
                    </div>
                    <div className="mt-2 flex justify-end border-t border-slate-50 pt-2">
                      <button
                        onClick={() => handleRowAction(txn.id)}
                        className="text-xs font-bold text-primary-600 hover:text-primary-800 transition-colors"
                      >
                        {t('details')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {transactions.length === 0 && (
              <div className="rounded-xl bg-white border border-slate-100 p-8 text-center text-slate-500">
                {t('noTransactionsFound')}
              </div>
            )}
          </div>
        ) : (
          <TransactionsTable
            data={transactions}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onRowAction={handleRowAction}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Pagination */}
      {transactions.length > 0 && totalCount > 0 && (
        <div className="mt-6 flex justify-end">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            itemLabel="transactions"
          />
        </div>
      )}

      {/* Footer Row - Insights & Heatmap */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Smart Insights Card */}
        <div className="lg:col-span-4">
          <SmartInsightsCard
            highlight="Manhattan Flagship"
            title={t('transactionsUp')}
            metric="22%"
            description={t('peakHoursStaffSuggestion')}
            ctaLabel={t('fullAnalysis')}
            onCtaClick={handleFullAnalysis}
          />
        </div>

        {/* Regional Heatmap */}
        <div className="lg:col-span-8">
          <RegionalHeatmap />
        </div>
      </div>

      {/* Detail Modal */}
      {selectedTxn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setSelectedTxn(null)}>
          <div className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">{t('transactionDetails')}</h3>
              <button onClick={() => setSelectedTxn(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="border-t border-slate-100 px-6 py-4 space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('transactionCode')}</span>
                <span className="text-sm font-bold text-slate-900">{selectedTxn.code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('status')}</span>
                <span className="text-sm font-bold text-slate-900">{paymentStatusLabel(selectedTxn.status, t)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('orderRef')}</span>
                <span className="text-sm font-semibold text-slate-950">{selectedTxn.orderRef}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('amount')}</span>
                <span className="text-sm font-extrabold text-slate-950">
                  {formatTxnAmount(selectedTxn.amount, selectedTxn.storeRegion)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('paymentMethod')}</span>
                <span className="text-sm font-semibold text-slate-700 uppercase">{selectedTxn.paymentMethod}</span>
              </div>
              {selectedTxn.paymentMasked && (
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">{t('providerTransId')}</span>
                  <span className="text-sm font-mono text-slate-600">{selectedTxn.paymentMasked}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('currencyUnit')}</span>
                <span className="text-sm text-slate-600">{selectedTxn.storeRegion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">{t('time')}</span>
                <span className="text-sm text-slate-600">{new Date(selectedTxn.timestamp).toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-slate-50 px-6 py-4 flex justify-end border-t border-slate-100">
              <button onClick={() => setSelectedTxn(null)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
}
